import type { StepFlowLang } from '@/components/step-flow/StepFlowShell.types';
import { NOTE_DETAIL_LABELS, NOTE_FORMAT_LABELS } from '@/lib/noteStyle';
import { PROFESSION_LABELS } from '@/lib/professions';
import { noteSamples } from '@/pages/onboarding/noteSamples';
import type { OnboardingCopy } from '@/pages/onboarding/onboarding.types';

export const ONBOARDING_TOTAL_STEPS = 4;

const is: OnboardingCopy = {
  stepLabel: (step, totalSteps) => `Skref ${step} af ${totalSteps}`,
  nav: { back: 'Til baka', next: 'Áfram', complete: 'Hefja notkun', submitting: 'Vista...' },
  profession: {
    greeting: (firstName) => (firstName ? `Velkomin/n, ${firstName}` : 'Velkomin/n'),
    subtitle: 'Nokkrar spurningar svo Skríban skrifi nótur eins og þú. Tekur um mínútu.',
    question: 'Hvert er starf þitt?',
    options: PROFESSION_LABELS,
    otherLabel: 'Starfsheiti',
    otherPlaceholder: 'T.d. lyfjafræðingur',
    specialtyQuestion: 'Sérgrein',
    specialties: {
      general_practice: 'Heimilislækningar',
      general_medicine: 'Almennar lækningar',
      psychiatry: 'Geðlækningar',
      pediatrics: 'Barnalækningar',
      internal_medicine: 'Lyflækningar',
      dermatology: 'Húðlækningar',
      gynecology: 'Kvensjúkdómalækningar',
      orthopedics: 'Bæklunarlækningar',
      other: 'Annað',
    },
    specialtyOtherLabel: 'Sérgrein',
    specialtyOtherPlaceholder: 'T.d. hjartalækningar',
  },
  work: {
    title: 'Hvernig vinnur þú?',
    subtitle: 'Við stillum upptöku og sniðmát eftir því hvernig viðtölin þín fara fram.',
    consultationQuestion: 'Hvers konar viðtöl tekur þú?',
    consultationHint: 'Veldu allt sem á við',
    consultationTypes: { in_person: 'Á stofu', phone: 'Símaviðtal', video: 'Myndsímtal', home_visit: 'Vitjun' },
    modeQuestion: 'Hvort notar þú meira?',
    modes: {
      conversation: { label: 'Samtal', description: 'Skríban hlustar á viðtalið og skrifar nótuna.' },
      dictation: { label: 'Diktering', description: 'Þú lest nótuna inn eftir viðtalið.' },
      both: { label: 'Hvort tveggja', description: 'Þú notar bæði eftir aðstæðum.' },
    },
  },
  detail: {
    title: 'Hvernig viltu hafa nóturnar?',
    subtitle: 'Allt byggir á sama viðtalinu. Þú getur breytt þessu fyrir hverja nótu.',
    sliderLabel: 'Hversu ítarleg á nótan að vera?',
    levels: NOTE_DETAIL_LABELS,
    formatLabel: 'Snið',
    formats: NOTE_FORMAT_LABELS,
    sampleHeader: 'Dæmi um nótu',
    sampleDescriptions: {
      short: 'Aðeins aðalatriðin',
      balanced: 'Helstu atriði í hverjum kafla',
      detailed: 'Öll klínískt viðeigandi smáatriði',
    },
    samples: noteSamples.is,
  },
  summary: {
    greeting: (firstName) => (firstName ? `Allt klárt, ${firstName}` : 'Allt klárt'),
    subtitle: 'Svona stillum við Skríbuna fyrir þig. Þú getur breytt þessu hvenær sem er í stillingum.',
    rows: { profession: 'Starf', consultations: 'Viðtöl', mode: 'Notar mest', detail: 'Ítarleiki' },
    edit: 'Breyta',
    signatureLabel: 'Undirskrift í bréfum',
    signatureHint: 'Valfrjálst',
    signaturePlaceholder: 'Nafn, starfsheiti, vinnustaður',
    signatureTitles: {
      professions: {
        doctor: 'læknir',
        nurse: 'hjúkrunarfræðingur',
        physiotherapist: 'sjúkraþjálfari',
        psychologist: 'sálfræðingur',
        midwife: 'ljósmóðir',
        occupational_therapist: 'iðjuþjálfi',
        speech_therapist: 'talmeinafræðingur',
        dietitian: 'næringarfræðingur',
        social_worker: 'félagsráðgjafi',
      },
      specialties: {
        general_practice: 'sérfræðingur í heimilislækningum',
        general_medicine: 'læknir',
        psychiatry: 'sérfræðingur í geðlækningum',
        pediatrics: 'sérfræðingur í barnalækningum',
        internal_medicine: 'sérfræðingur í lyflækningum',
        dermatology: 'sérfræðingur í húðlækningum',
        gynecology: 'sérfræðingur í kvensjúkdómalækningum',
        orthopedics: 'sérfræðingur í bæklunarlækningum',
        other: 'læknir',
      },
    },
    piiNext: 'Næst: stutt fræðsla um persónuvernd áður en þú tekur fyrstu upptöku.',
  },
  toast: { errorTitle: 'Villa', errorDescription: 'Ekki tókst að vista svörin. Reyndu aftur.' },
};

const en: OnboardingCopy = {
  stepLabel: (step, totalSteps) => `Step ${step} of ${totalSteps}`,
  nav: { back: 'Back', next: 'Continue', complete: 'Get started', submitting: 'Saving...' },
  profession: {
    greeting: (firstName) => (firstName ? `Welcome, ${firstName}` : 'Welcome'),
    subtitle: 'A few questions so Skríban writes notes the way you do. Takes about a minute.',
    question: 'What is your profession?',
    options: {
      doctor: 'Doctor',
      nurse: 'Nurse',
      physiotherapist: 'Physiotherapist',
      psychologist: 'Psychologist',
      midwife: 'Midwife',
      occupational_therapist: 'Occupational therapist',
      speech_therapist: 'Speech therapist',
      dietitian: 'Dietitian',
      social_worker: 'Social worker',
      other: 'Other',
    },
    otherLabel: 'Job title',
    otherPlaceholder: 'E.g. pharmacist',
    specialtyQuestion: 'Specialty',
    specialties: {
      general_practice: 'General practice',
      general_medicine: 'General medicine',
      psychiatry: 'Psychiatry',
      pediatrics: 'Paediatrics',
      internal_medicine: 'Internal medicine',
      dermatology: 'Dermatology',
      gynecology: 'Gynaecology',
      orthopedics: 'Orthopaedics',
      other: 'Other',
    },
    specialtyOtherLabel: 'Specialty',
    specialtyOtherPlaceholder: 'E.g. cardiology',
  },
  work: {
    title: 'How do you work?',
    subtitle: 'We set up recording and templates around how your consultations happen.',
    consultationQuestion: 'What kind of consultations do you have?',
    consultationHint: 'Select all that apply',
    consultationTypes: { in_person: 'In clinic', phone: 'Phone', video: 'Video call', home_visit: 'Home visit' },
    modeQuestion: 'Which do you use more?',
    modes: {
      conversation: { label: 'Conversation', description: 'Skríban listens to the consultation and writes the note.' },
      dictation: { label: 'Dictation', description: 'You dictate the note after the consultation.' },
      both: { label: 'Both', description: 'You use both, depending on the situation.' },
    },
  },
  detail: {
    title: 'How do you want your notes?',
    subtitle: 'Everything is based on the same consultation. You can change this for each note.',
    sliderLabel: 'How detailed should the note be?',
    levels: { short: 'Short', balanced: 'Balanced', detailed: 'Detailed' },
    formatLabel: 'Format',
    formats: { template: 'Follow template', prose: 'Prose', bullets: 'Bullet points' },
    sampleHeader: 'Example note',
    sampleDescriptions: {
      short: 'Only the essentials',
      balanced: 'The key points in each section',
      detailed: 'All clinically relevant detail',
    },
    samples: noteSamples.en,
  },
  summary: {
    greeting: (firstName) => (firstName ? `All set, ${firstName}` : 'All set'),
    subtitle: "This is how we'll set up Skríban for you. You can change this any time in settings.",
    rows: { profession: 'Profession', consultations: 'Consultations', mode: 'Uses most', detail: 'Detail' },
    edit: 'Edit',
    signatureLabel: 'Letter signature',
    signatureHint: 'Optional',
    signaturePlaceholder: 'Name, title, workplace',
    signatureTitles: {
      professions: {
        doctor: 'doctor',
        nurse: 'nurse',
        physiotherapist: 'physiotherapist',
        psychologist: 'psychologist',
        midwife: 'midwife',
        occupational_therapist: 'occupational therapist',
        speech_therapist: 'speech therapist',
        dietitian: 'dietitian',
        social_worker: 'social worker',
      },
      specialties: {
        general_practice: 'specialist in general practice',
        general_medicine: 'doctor',
        psychiatry: 'specialist in psychiatry',
        pediatrics: 'specialist in paediatrics',
        internal_medicine: 'specialist in internal medicine',
        dermatology: 'specialist in dermatology',
        gynecology: 'specialist in gynaecology',
        orthopedics: 'specialist in orthopaedics',
        other: 'doctor',
      },
    },
    piiNext: 'Next: a short privacy briefing before your first recording.',
  },
  toast: { errorTitle: 'Error', errorDescription: 'Could not save your answers. Please try again.' },
};

export const onboardingContent: Record<StepFlowLang, OnboardingCopy> = { is, en };
